import { toDate } from "helpers/dateHelper";
import { Avatar, MenuItem, Paper, Select } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { Ticket, TicketPriority, TicketType } from "interfaces/entities/ticket.model";
import { getFullName } from "helpers/utils";
import { Link } from "react-router-dom";

interface TicketMainInfoProps {
    ticket: Ticket | null;
    members: any[];
    categories: any[];
    branches: any[];
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">
            {title}
        </div>
        {children}
    </Paper>
);

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-center gap-3">
        <span className="text-xs text-gray-600 w-28 flex-shrink-0">{label}</span>
        <div className="flex-1 min-w-0">{children}</div>
    </div>
);

export const TicketMainInfo = ({
    ticket = null,
    members,
    categories,
    branches,
}: TicketMainInfoProps) => {
    const { t } = useTranslation();
    const { control } = useFormContext();

    const customerName = ticket?.customer ? getFullName(ticket.customer) : "";
    const customerInitial = (customerName || "?")[0]?.toUpperCase();

    return (
        <div className="flex flex-col gap-3">
            {ticket && (
                <Section title="Клиент">
                    {ticket.customerID ? (
                        <div className="flex items-center gap-3">
                            <Avatar
                                sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: "#E0E7FF",
                                    color: "#3730A3",
                                    fontSize: 16,
                                    fontWeight: 600,
                                }}
                            >
                                {customerInitial}
                            </Avatar>
                            <div className="flex flex-col min-w-0">
                                <Link
                                    target="_blank"
                                    to={`/admin/customers/view/${ticket.customerID}`}
                                    className="font-medium text-sm truncate"
                                >
                                    {customerName || "Клиент"}
                                </Link>
                                {ticket.customer?.phone && (
                                    <span className="text-xs text-gray-500">
                                        {ticket.customer.phone}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <span className="text-sm text-gray-400">Не указан</span>
                    )}
                </Section>
            )}

            <Section title="Свойства">
                <div className="flex flex-col gap-3">
                    <Row label="Приоритет">
                        <Controller
                            control={control}
                            name="priority"
                            render={({ field }) => (
                                <Select {...field} size="small" fullWidth>
                                    {Object.values(TicketPriority).map((p: TicketPriority) => (
                                        <MenuItem key={p} value={p}>
                                            {t(`priority.${p}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </Row>
                    <Row label="Тип обращения">
                        <Controller
                            control={control}
                            name="type"
                            render={({ field }) => (
                                <Select {...field} size="small" fullWidth>
                                    {Object.values(TicketType).map((type: TicketType) => (
                                        <MenuItem key={type} value={type}>
                                            {t(`ticket.type.${type}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </Row>
                    <Row label="Тематика">
                        <Controller
                            control={control}
                            name="categoryID"
                            render={({ field }) => (
                                <Select {...field} size="small" fullWidth displayEmpty>
                                    <MenuItem value="">
                                        <span className="text-gray-400">— не выбрано —</span>
                                    </MenuItem>
                                    {categories?.map((category: any) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </Row>
                    <Row label="Филиал">
                        <Controller
                            control={control}
                            name="branchID"
                            render={({ field }) => (
                                <Select {...field} size="small" fullWidth displayEmpty>
                                    <MenuItem value="">
                                        <span className="text-gray-400">— не выбрано —</span>
                                    </MenuItem>
                                    {branches?.map((branch: any) => (
                                        <MenuItem key={branch.id} value={branch.id}>
                                            {branch.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </Row>
                    <Row label="Ответственный">
                        <Controller
                            control={control}
                            name="memberID"
                            render={({ field }) => (
                                <Select {...field} size="small" fullWidth displayEmpty>
                                    <MenuItem value="">
                                        <span className="text-gray-400">— не назначен —</span>
                                    </MenuItem>
                                    {members?.map((member: any) => (
                                        <MenuItem key={member.id} value={member.id}>
                                            {member.name || member.userID}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </Row>
                </div>
            </Section>

            {ticket?.createdAt && (
                <Section title="Информация">
                    <div className="flex flex-col gap-2 text-sm">
                        <div className="flex justify-between gap-2">
                            <span className="text-gray-500">Создан</span>
                            <span>{toDate(ticket.createdAt)}</span>
                        </div>
                        {ticket.closeReason && (
                            <div className="flex justify-between gap-2">
                                <span className="text-gray-500 flex-shrink-0">
                                    Причина закрытия
                                </span>
                                <span className="text-right">
                                    {t(`ticket.closeReason.${ticket.closeReason}`, {
                                        defaultValue: ticket.closeReason,
                                    })}
                                </span>
                            </div>
                        )}
                    </div>
                </Section>
            )}
        </div>
    );
};
